import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/context/auth-context'
import { Menu, X, Sun, Moon, ChevronDown } from 'lucide-react'
import { navLinks, dropdownLinks } from '@/data/mock-data'
import { cn } from '@/lib/utils'
import MobileNav from './mobile-nav'
import { useTheme } from '@/contexts/theme-context'

export default function Header() {
    const [isScrolled, setIsScrolled] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const { theme, toggleTheme } = useTheme()
    const navigate = useNavigate()
    const { isAuthenticated, user, logout } = useAuth()

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20)
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return (
        <>
            <header
                className={cn(
                    'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
                    isScrolled
                        ? 'glass-dark shadow-lg'
                        : 'bg-transparent'
                )}
            >
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between h-20">
                        {/* Logo */}
                        <Link to="/" className="flex items-center space-x-3 group">
                            <img
                                src="/ff-logo.png"
                                alt="Future Foundeer"
                                className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
                            />
                            <span className="text-xl font-display font-bold text-theme-primary hidden sm:block">
                                Future Foundeer
                            </span>
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex items-center space-x-8">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className="text-sm font-medium text-theme-secondary hover:text-theme-primary transition-colors duration-200 relative group"
                                >
                                    {link.label}
                                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-blue group-hover:w-full transition-all duration-300" />
                                </Link>
                            ))}

                            {/* Explore Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    onMouseEnter={() => setIsDropdownOpen(true)}
                                    className={cn(
                                        "flex items-center gap-1 text-sm font-medium transition-colors duration-200 relative group",
                                        isDropdownOpen ? "text-theme-primary" : "text-theme-secondary hover:text-theme-primary"
                                    )}
                                >
                                    Explore
                                    <ChevronDown className={cn(
                                        "h-3.5 w-3.5 transition-transform duration-200",
                                        isDropdownOpen && "rotate-180"
                                    )} />
                                    <span className={cn(
                                        "absolute -bottom-1 left-0 h-0.5 bg-primary-blue transition-all duration-300",
                                        isDropdownOpen ? "w-full" : "w-0 group-hover:w-full"
                                    )} />
                                </button>

                                {/* Dropdown Panel */}
                                <div
                                    onMouseLeave={() => setIsDropdownOpen(false)}
                                    className={cn(
                                        "absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-all duration-200",
                                        isDropdownOpen
                                            ? "opacity-100 translate-y-0 pointer-events-auto"
                                            : "opacity-0 -translate-y-2 pointer-events-none"
                                    )}
                                >
                                    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl shadow-black/10 dark:shadow-black/30 overflow-hidden min-w-[200px]">
                                        {dropdownLinks.map((link) => (
                                            <Link
                                                key={link.path}
                                                to={link.path}
                                                onClick={() => setIsDropdownOpen(false)}
                                                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-theme-secondary hover:text-primary-blue hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors duration-150 border-b border-slate-100 dark:border-slate-700 last:border-b-0"
                                            >
                                                {link.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </nav>

                        {/* CTA Buttons */}
                        <div className="hidden lg:flex items-center space-x-4">
                            <button
                                onClick={toggleTheme}
                                className="p-2 text-theme-secondary hover:text-theme-primary transition-colors duration-200"
                                aria-label="Toggle theme"
                            >
                                {theme === 'dark' ? (
                                    <Sun className="h-5 w-5" />
                                ) : (
                                    <Moon className="h-5 w-5" />
                                )}
                            </button>

                            {isAuthenticated ? (
                                <>
                                    <button
                                        onClick={() => navigate(user?.role === 'mentor' ? '/mentor/dashboard' : '/dashboard')}
                                        className="px-4 py-2 text-sm font-medium text-theme-primary hover:text-primary-blue transition-colors duration-200"
                                    >
                                        Dashboard
                                    </button>
                                    <button
                                        onClick={() => { logout(); navigate('/') }}
                                        className="px-6 py-2.5 text-sm font-medium text-white bg-primary-blue hover:bg-blue-600 rounded-lg glow-subtle hover:glow-primary transition-all duration-200"
                                    >
                                        Logout
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Link to="/login">
                                        <button className="px-4 py-2 text-sm font-medium text-theme-primary hover:text-primary-blue transition-colors duration-200">
                                            Login
                                        </button>
                                    </Link>
                                    <Link to="/signup">
                                        <button className="px-6 py-2.5 text-sm font-medium text-white bg-primary-blue hover:bg-blue-600 rounded-lg glow-subtle hover:glow-primary transition-all duration-200">
                                            Sign Up
                                        </button>
                                    </Link>
                                </>
                            )}
                        </div>

                        {/* Mobile Controls */}
                        <div className="flex items-center gap-2 lg:hidden">
                            <button
                                onClick={toggleTheme}
                                className="p-2 text-theme-secondary hover:text-theme-primary transition-colors duration-200"
                                aria-label="Toggle theme"
                            >
                                {theme === 'dark' ? (
                                    <Sun className="h-5 w-5" />
                                ) : (
                                    <Moon className="h-5 w-5" />
                                )}
                            </button>

                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="p-2 text-theme-primary hover:text-primary-blue transition-colors"
                                aria-label="Toggle menu"
                            >
                                {isMobileMenuOpen ? (
                                    <X className="h-6 w-6" />
                                ) : (
                                    <Menu className="h-6 w-6" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </header >

            {/* Mobile Navigation */}
            < MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)
            } />
        </>
    )
}
